// Appareils · Registre de la flotte (SA-6) — le POOL de matériel Orgazone : ajouter au parc, affecter à un
// magasin (régime location courte/longue ou achat client), retour au parc, révocation. C'est l'étape
// incontournable avant d'appairer une caisse ou un Mobile Partner : on affecte depuis un inventaire réel.
// ⚠️ §23.0 : on n'affiche QUE des données réelles (table `device`). Le heartbeat (« hors-ligne ») et le wipe
// à distance dépendent d'un appareil qui remonte — non branché → on ne SIMULE pas d'état en ligne/hors-ligne
// (lastSeenAt reste null tant qu'aucun appareil ne se signale), on n'affiche que l'état d'affectation.
import { dateCompact, dateTimeLong, dateTimeShort } from '@orgazone/date-format';
import { useEffect, useId, useRef, useState, type CSSProperties, type FormEvent, type MouseEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import qrcode from 'qrcode-generator';
import { api, type DeviceRow, type StoreRow, type DevicePairingState } from '../api';
import { alert, confirm, showToast, useOverlayPanel } from '@orgazone/ui-web';
import { equipmentTypesFrom, NATURE_HINT, NATURE_ICON, NO_NATURE_HINT, type EquipmentTypeOption } from '../equipment-types';
import { glassBoxSt, glassOverlaySt } from '../glass-modal';
import { RentalModal } from './rental-modal';
import { DeviceName, deviceRef, deviceTitle } from '../device-name';
import { EmptyState, Icon } from '@orgazone/ui-web';

// QR de mise en service — SVG inline (lib pure JS, aucun réseau). Correction d'erreur M, modules noirs/fond blanc.
export function qrSvg(text: string): string {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
}


import { Chip, formPanelRef, Pill as UiPill, daysLeft, REGIME } from '../ui';
import { DataTable, type DtAction } from '../data-table';
// Pastille de ligne de parc — géométrie « row » (cf. ../ui) ; la signature locale reste inchangée.
// La capsule de la maquette (`pill()` : 11 px, graisse 600, 23 px de haut) — celle que le tableau du parc dessine.
const Pill = ({ bg, fg, children }: { bg: string; fg: string; children: ReactNode }) => <UiPill bg={bg} fg={fg} shape="tight">{children}</UiPill>;
const fieldSt: CSSProperties = { width: '100%', padding: '9px 12px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-control)' };
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDay = dateCompact;      // « 04/08/26 »
const fmtDateTime = dateTimeLong; // « 4 août 2026 à 14:32 »

// État d'affectation d'un appareil (libellé + pastille) — dérivé du statut réel, jamais du réseau (§23.0).
function stateOf(d: DeviceRow): { label: string; bg: string; fg: string } {
  if (d.status === 'available') return { label: 'disponible', bg: 'var(--in-bg)', fg: 'var(--in-fg)' };
  if (d.status === 'revoked') return { label: 'révoqué', bg: 'var(--da-bg)', fg: 'var(--da-fg)' };
  if (d.status === 'returned') return { label: 'retourné', bg: 'var(--ne-bg)', fg: 'var(--ne-fg)' };
  // « en location » pour une location courte seulement : une location longue ou un achat sont un appareil en service.
  return { label: d.rentalType === 'courte' ? 'en location' : 'en service', bg: 'var(--ok-bg)', fg: 'var(--ok-fg)' };
}

// ── Modale : ENTRÉE AU PARC (statut « disponible », sans magasin), ou MODIFICATION d'un appareil du parc — un formulaire ─
// ⚠️ DEUX ÉTAPES, et la seconde ne se valide qu'au CLIC. Le numéro de série se saisit à la douchette, qui
//    envoie Entrée en fin de scan : un formulaire qui enregistrerait sur Entrée ferait entrer au parc — ou corrigerait —
//    un appareil au numéro erroné dès le premier scan raté. Entrée ouvre donc le récapitulatif ; le récapitulatif n'a
//    aucun champ et ne place le focus sur aucun bouton, donc un second Entrée n'y fait rien.
// ⚠️ LE TYPE SE CHOISIT DANS LE RÉFÉRENTIEL « Types d'équipement », jamais dans une liste écrite ici : c'est sa
//    NATURE qui décide de ce que l'appareil déclenchera à l'affectation (numéro de caisse, code « MP01 », licence),
//    et un nouveau matériel s'ajoute au référentiel sans livraison. Sans type, aucun ajout.
// ⚠️ LE TYPE D'UN APPAREIL AFFECTÉ EST FIGÉ : c'est sa nature qui a décidé de son numéro de caisse ou de son code
//    magasin. On en change après un retour au parc. (Le serveur, lui, tolère un type de MÊME nature : l'écran est
//    plus strict que la règle, jamais l'inverse.)
// ⚠️ LE NUMÉRO DE SÉRIE GARDE SA CASSE : la mise en service le compare tel quel à celui que déclare la machine.
export function DeviceForm({ rows, device, onClose, onDone }: {
  rows: DeviceRow[];
  /** L'appareil à modifier ; absent, le formulaire en ajoute un au parc. */
  device?: DeviceRow;
  onClose: () => void;
  onDone: (notice: string) => void;
}) {
  const editing = device !== undefined;
  // `null` = lecture en cours ; une lecture en échec se DIT (le motif réel), elle ne retombe sur aucune liste.
  const [types, setTypes] = useState<EquipmentTypeOption[] | null>(null);
  const [typesErr, setTypesErr] = useState<string | null>(null);
  const [typeCode, setTypeCode] = useState(device?.equipmentType ?? '');
  const [model, setModel] = useState(device?.model ?? '');
  const [serial, setSerial] = useState(device?.serial ?? '');
  const [name, setName] = useState(device?.name ?? '');
  const [step, setStep] = useState<'form' | 'recap'>('form');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const serialField = useRef<HTMLInputElement | null>(null);
  const cameBack = useRef(false);
  const titleId = useId();
  const typeGroup = useId();
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Refusé pendant l'enregistrement, comme le clic sur le fond.
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });

  useEffect(() => {
    let live = true;
    api.referentials()
      .then((r) => {
        if (!live) return;
        const list = equipmentTypesFrom(r.referentials);
        setTypes(list);
        // ⚠️ Seul l'AJOUT propose un type d'office : à la modification, un appareil sans type le reste tant qu'on
        //    n'en choisit pas un — le retyper en silence changerait ce qu'il déclenche.
        if (!editing) setTypeCode((c) => c || list[0]?.code || '');
      })
      .catch((e: unknown) => { if (live) setTypesErr((e as Error).message); });
    return () => { live = false; };
  }, [editing]);
  const typeLocked = editing && device.status === 'assigned';
  const type = types?.find((x) => x.code === typeCode) ?? null;
  const draft = { type, model: model.trim(), serial: serial.trim(), name: name.trim() };
  // Ce qui change, à la modification — l'ajout « change » tout.
  const changed = {
    type: !editing || typeCode !== (device.equipmentType ?? ''),
    model: !editing || draft.model !== (device.model ?? ''),
    serial: !editing || draft.serial !== (device.serial ?? ''),
    name: !editing || draft.name !== (device.name ?? ''),
  };
  const anyChange = changed.type || changed.model || changed.serial || changed.name;
  // Un appareil sans type peut le rester à la modification ; à l'ajout, le type est obligatoire.
  const typeless = editing && device.equipmentType === null;
  const typeReady = draft.type !== null || (typeless && typeCode === '');
  // ⚠️ Le nom est exigé à l'ajout ET à la modification : un appareil entré au parc avant lui en reçoit un ici.
  const ready = typeReady && draft.name !== '' && anyChange;
  // ⚠️ Le numéro de série est UNIQUE au parc, révoqués compris : la base refuserait l'écriture. Le dire ici
  //    évite d'offrir un bouton dont l'issue est connue d'avance — et nomme l'appareil qui le porte déjà.
  const taken = draft.serial ? rows.find((d) => d.id !== device?.id && d.serial === draft.serial) ?? null : null;

  // Au récapitulatif, le focus va au cadre (aucune action) ; au retour, le numéro de série est SÉLECTIONNÉ,
  // pour qu'un nouveau scan remplace celui qu'on vient de refuser au lieu de s'y ajouter.
  useEffect(() => {
    if (step === 'recap') panelRef.current?.focus();
    else if (cameBack.current) { serialField.current?.focus(); serialField.current?.select(); }
  }, [step, panelRef]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setErr(null); setStep('recap');
  };
  const back = () => { cameBack.current = true; setErr(null); setStep('form'); };
  const save = async () => {
    if (!ready) return;
    setErr(null); setBusy(true);
    try {
      if (editing) {
        await api.editDevice(device.id, { name: draft.name, equipmentType: typeCode || null, model: draft.model || null, serial: draft.serial || null });
        onDone(`${draft.name} modifié.`);
      } else {
        await api.addDevice({ name: draft.name, equipmentType: draft.type!.code, model: draft.model || undefined, serial: draft.serial || undefined });
        onDone(`${draft.name} ajouté au parc (disponible).`);
      }
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const recap = step === 'recap';
  const NONE = '— non renseigné';
  /** Une ligne du récapitulatif : la valeur ENREGISTRÉE, puis, barrée, celle qu'elle remplace. */
  const recapRow = (k: string, v: string, was: string | null, opts: { mono?: boolean; serial?: boolean } = {}) => (
    <div key={k} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'baseline', padding: '10px 14px', borderBottom: '1px solid var(--ink100)' }}>
      <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', flex: '0 0 130px' }}>{k}</span>
      <span className={opts.mono ? 'mono' : undefined} data-recap-serial={opts.serial && v !== NONE ? '' : undefined}
        style={{ fontSize: opts.mono ? 'var(--t-dense)' : 'var(--t-body)', fontWeight: 600, color: v.startsWith('—') ? 'var(--ink400)' : 'var(--ink900)', minWidth: 0, overflowWrap: 'anywhere' }}>
        {v}
      </span>
      {was !== null && <s className="mono" data-recap-before style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)' }}>{was}</s>}
    </div>
  );
  const typeNow = draft.type?.label ?? (typeless ? 'sans type' : '—');
  const typeBefore = device ? (device.equipmentType ? device.family : 'sans type') : null;
  const foot = !typeReady ? 'Choisissez un type d’équipement.' : draft.name === '' ? 'Donnez un nom à l’appareil.' : !anyChange ? 'Aucune modification à enregistrer.' : '';
  const ctaOff: CSSProperties = { background: 'var(--ink200)', color: 'var(--ink400)', boxShadow: 'none', cursor: 'not-allowed' };
  const labelSt: CSSProperties = { display: 'block', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 5 };

  // ⚠️ AU RÉCAPITULATIF, LA BOÎTE N'EST PLUS UN FORMULAIRE : aucun Entrée ne peut y soumettre quoi que ce soit, et le
  //    focus va au cadre — l'enregistrement ne part qu'au clic sur « Confirmer ».
  const box: CSSProperties = { ...glassBoxSt(580), outline: 'none' };
  const stop = (e: MouseEvent) => e.stopPropagation();
  const content = (
    <>
      {/* ── En-tête : où l'on est (le parc), ce qu'on fait, ce que ça engage ── */}
      <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)', display: 'flex', alignItems: 'flex-start', gap: 14, flexShrink: 0 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink500)' }}>
            {editing ? `Parc · ${device.id.slice(0, 8)}` : 'Parc · nouvel appareil'}
          </div>
          <h3 id={titleId} style={{ margin: '5px 0 0', fontSize: 16, fontWeight: 700, letterSpacing: '-.02em' }}>
            {recap
              ? (editing ? 'Confirmer la modification' : 'Confirmer l’ajout au parc')
              : (editing ? `Modifier ${deviceRef(device)}` : 'Ajouter un appareil au parc')}
          </h3>
          <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.5, textWrap: 'pretty' }}>
            {recap
              ? 'Relisez avant d’enregistrer : le numéro de série sert ensuite à reconnaître la machine au moment de l’affecter.'
              : editing
                ? 'Le formulaire de l’entrée au parc, prérempli. L’identifiant ne change pas.'
                : 'L’appareil entre au parc « disponible », sans magasin. On l’affecte ensuite depuis cette page ou depuis la fiche d’un magasin.'}
          </div>
        </div>
        <button type="button" className="btn-icon" onClick={onClose} disabled={busy} title="Fermer" aria-label="Fermer">✕</button>
      </div>

      {/* ── Corps : le formulaire, puis le récapitulatif à relire ── */}
      <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!recap && <>
          {typesErr
            ? <div className="err" role="alert">Les types d'équipement n'ont pas pu être lus : {typesErr}</div>
            : types === null
              ? <p className="note" role="status">Lecture des types d'équipement…</p>
              : !editing && types.length === 0
                ? <div className="err" role="alert">Aucun type d'équipement : créez-en un dans Référentiels → Types d'équipement avant d'ajouter un appareil.</div>
                : (
                  <div role="group" aria-labelledby={typeGroup}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <span id={typeGroup} style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink600)' }}>Type d’équipement</span>
                      {!typeless && <span style={{ fontSize: 'var(--t-micro)', fontWeight: 600, color: 'var(--teal-500)' }}>requis</span>}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                      {types.map((x) => {
                        const on = x.code === typeCode;
                        return (
                          <button key={x.code} type="button" data-type-tile={x.code} aria-pressed={on} disabled={typeLocked}
                            className={on ? 'row-pick selected' : 'row-pick'} onClick={() => setTypeCode(x.code)}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 11, width: '100%', padding: '12px 13px', textAlign: 'left', color: 'inherit', font: 'inherit',
                              ...(on ? null : { background: 'var(--panel)' }), ...(typeLocked ? { cursor: 'not-allowed', opacity: on ? 1 : 0.55 } : null) }}>
                            <span style={{ width: 34, height: 34, borderRadius: 'var(--r-ctrl)', display: 'grid', placeItems: 'center', flexShrink: 0,
                              background: on ? 'var(--ok-bg)' : 'var(--ink100)', color: on ? 'var(--ok-fg)' : 'var(--ink500)' }}>
                              <Icon name={x.nature ? NATURE_ICON[x.nature] : 'box'} size={17} />
                            </span>
                            <span style={{ display: 'block', minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink900)' }}>{x.label}</span>
                              <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 2, lineHeight: 1.45 }}>{x.nature ? NATURE_HINT[x.nature] : NO_NATURE_HINT}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <div data-type-note style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 8, lineHeight: 1.45 }}>
                      {typeLocked
                        ? `Type figé : l’appareil est affecté à ${device.storeName ?? 'un magasin'}. Faites d’abord un retour au parc pour en changer.`
                        : 'Liste tenue dans Référentiels → Types d’équipement.'}
                    </div>
                  </div>
                )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <div>
              <label htmlFor="mdl" style={labelSt}>Modèle <span style={{ fontWeight: 500, color: 'var(--ink400)' }}>(optionnel)</span></label>
              <input id="mdl" className="field-input" value={model} onChange={(e) => setModel(e.target.value)} placeholder="ex. Zebra TC22"
                style={{ minHeight: 36, height: 36, background: 'var(--elev)' }} />
            </div>
            <div>
              <label htmlFor="srl" style={labelSt}>Numéro de série <span style={{ fontWeight: 500, color: 'var(--ink400)' }}>(optionnel)</span></label>
              <input id="srl" ref={serialField} className="field-input mono" value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="tel qu’imprimé sur l’étiquette"
                style={{ minHeight: 36, height: 36, background: 'var(--elev)', fontSize: 'var(--t-body)', letterSpacing: '.02em' }} />
            </div>
          </div>
          <div>
            <label htmlFor="dnm" style={labelSt}>Nom de l’appareil <span style={{ fontSize: 'var(--t-micro)', fontWeight: 600, color: 'var(--teal-500)', marginLeft: 4 }}>requis</span></label>
            <input id="dnm" className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="ex. Chalet 5 caisse 1" aria-describedby="dnm-note"
              style={{ minHeight: 36, height: 36, background: 'var(--elev)' }} />
            <div id="dnm-note" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 5, lineHeight: 1.45 }}>
              Le nom qui repère l’appareil dans le parc et dans le magasin. Il se change à tout moment ; l’identifiant, jamais.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 14px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--in-bg)' }}>
            <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 5, background: 'color-mix(in srgb, var(--in-fg) 22%, transparent)', color: 'var(--in-fg)', display: 'grid', placeItems: 'center', fontSize: 'var(--t-meta)', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>i</span>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--in-fg)', lineHeight: 1.55 }}>
              Le numéro de série se saisit ici, une seule fois : l’assistant d’affectation l’affiche ensuite sur chaque appareil disponible, pour expédier la bonne machine.
            </div>
          </div>
        </>}

        {recap && <>
          <div style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', overflow: 'hidden', background: 'var(--panel)' }}>
            {recapRow('Type', typeNow, editing && changed.type ? typeBefore : null)}
            {recapRow('Modèle', draft.model || NONE, editing && changed.model ? (device.model || NONE) : null)}
            {recapRow('Numéro de série', draft.serial || NONE, editing && changed.serial ? (device.serial || NONE) : null, { mono: true, serial: true })}
            {recapRow('Nom de l’appareil', draft.name || NONE, editing && changed.name ? (device.name || NONE) : null)}
            {!editing && recapRow('Statut à l’entrée', 'disponible · aucun magasin', null)}
          </div>
          {taken && (
            <div role="alert" style={{ padding: '9px 12px', borderRadius: 'var(--r-sm)', background: 'var(--wa-bg)', color: 'var(--wa-fg)', fontSize: 'var(--t-note)' }}>
              Ce numéro de série est déjà au parc : {taken.family}{taken.model ? ` · ${taken.model}` : ''}, {stateOf(taken).label}.
              {taken.status === 'revoked'
                ? ' Supprimez d’abord l’appareil révoqué pour le réutiliser.'
                : editing ? ' Un numéro de série ne désigne qu’un appareil.' : ' Un même appareil ne s’ajoute pas deux fois.'}
            </div>
          )}
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.55 }}>
            {editing
              ? 'Modification journalisée avec votre compte.'
              : draft.serial
                ? 'L’appareil apparaîtra aussitôt parmi les disponibles de l’assistant d’affectation.'
                : 'Sans numéro de série, l’assistant d’affectation n’affichera que le modèle : vérifiez l’étiquette au moment d’expédier.'}
          </div>
        </>}
        {err && <div className="err">{err}</div>}
      </div>

      {/* ── Pied : revenir, ce qui manque encore, avancer ── */}
      <div style={{ padding: '14px 22px', borderTop: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: 'var(--ink-50)', flexShrink: 0 }}>
        <button type="button" className="btn-ghost" onClick={recap ? back : onClose} disabled={busy}>{recap ? '← Retour' : 'Annuler'}</button>
        <div style={{ flex: '1 1 140px', minWidth: 0, fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.45 }}>{foot}</div>
        {recap
          ? (
            <button type="button" className="btn-cta" onClick={() => void save()} disabled={busy || taken !== null} style={busy || taken !== null ? ctaOff : undefined}>
              {busy ? (editing ? 'Enregistrement…' : 'Ajout…') : 'Confirmer'}
            </button>
          )
          : (
            <button type="submit" className="btn-cta" disabled={!ready} style={ready ? undefined : ctaOff}>
              {editing ? 'Enregistrer' : 'Ajouter au parc'}
            </button>
          )}
      </div>
    </>
  );
  return (
    <div style={glassOverlaySt} onClick={busy ? undefined : onClose}>
      {recap
        ? <div style={box} ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onClick={stop}>{content}</div>
        : <form style={box} ref={formPanelRef(panelRef)} role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={stop} onSubmit={submit}>{content}</form>}
    </div>
  );
}

// ── Modale : RETOUR AU PARC (maquette « wipe ») — l'appareil quitte son magasin. La confirmation se
// fait en RECOPIANT son identifiant : c'est le seul geste de l'écran qui touche des données client
// encore présentes sur le matériel, il ne doit pas pouvoir être fait par réflexe. Exporté : la fiche magasin l'ouvre aussi. ─
export function ReturnDevice({ dev, onClose, onDone }: { dev: DeviceRow; onClose: () => void; onDone: (msg: string) => void }) {
  const ident = dev.serial ?? dev.model ?? dev.id.slice(0, 8);
  const [typed, setTyped] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Refusé pendant le retour, comme le clic sur le fond. Le focus va à la confirmation par ce socle, jamais
  // par `autoFocus` : posé avant lui, il serait retenu comme déclencheur et le focus ne reviendrait nulle part.
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });
  const titleId = useId();
  const ok = typed.trim().toLowerCase() === ident.trim().toLowerCase();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.deviceAction(dev.id, { action: 'unassign' });
      onDone(`${deviceRef(dev)} rentré au parc (disponible) — jeton d'appairage révoqué.`);
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={busy ? undefined : onClose}>
      <form className="modal" ref={formPanelRef(panelRef)} role="dialog" aria-modal="true" aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3 id={titleId}>Retour au parc — {deviceRef(dev)}</h3>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginTop: -4, marginBottom: 10 }}>
          Détaché de <b style={{ color: 'var(--ink700)' }}>{dev.storeName ?? 'son magasin'}</b> et repassé en « disponible ».
        </div>
        {/* Conséquences ÉNONCÉES : ce que le retour fait, et ce qu'il ne fait PAS (§23.0). */}
        <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 'var(--t-note)', color: 'var(--ink600)', lineHeight: 1.7 }}>
          <li>Le jeton d'appairage devient caduc : l'appareil devra être re-mis en service pour encaisser.</li>
          <li>Son numéro de caisse est libéré — il en recevra un nouveau à la prochaine affectation.</li>
          <li><b style={{ color: 'var(--wa-fg)' }}>L'effacement des données locales n'est pas commandé d'ici</b> : il se fait sur l'appareil, avant réaffectation. La Console ne peut pas le prouver, elle ne le prétend donc pas.</li>
        </ul>
        <label htmlFor="wipe-id">Confirmez en écrivant <b className="mono">{ident}</b></label>
        <input id="wipe-id" className="mono" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={ident}
          style={{ ...fieldSt, borderColor: ok ? 'var(--ok-fg)' : 'var(--ink200)' }} />
        {err && <div className="err">{err}</div>}
        <div className="modalfoot">
          <button type="button" className="ghost btn-ghost" onClick={onClose} disabled={busy}>Annuler</button>
          <button type="submit" className="btn danger" disabled={!ok || busy}>{busy ? 'Retour…' : 'Retour au parc'}</button>
        </div>
      </form>
    </div>
  );
}

// ── Modale : affecter un appareil disponible à un magasin (+ régime + dates de location) ──────────
// ⚠️ Exporté : réutilisé par l'écran Locations (« Nouvelle location ») — règle de 2, jamais recopié.
export function AssignDevice({ dev, stores, presetStoreId, onClose, onDone }: { dev: DeviceRow; stores: StoreRow[] | null; presetStoreId?: string; onClose: () => void; onDone: (notice: string) => void }) {
  /** Le titre du choix de régime, que le groupe de boutons désigne. */
  const regimeId = useId();
  const [storeId, setStoreId] = useState(presetStoreId ?? '');
  const [rentalType, setRentalType] = useState<'courte' | 'longue' | 'achat'>('courte');
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Le focus va au choix du magasin par ce socle, jamais par `autoFocus` : posé avant lui, il serait
  // retenu comme déclencheur et le focus ne reviendrait nulle part.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const titleId = useId();
  const isRental = rentalType !== 'achat';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!storeId) { setErr('Choisissez un magasin.'); return; }
    setErr(null); setBusy(true);
    try {
      await api.deviceAction(dev.id, {
        action: 'assign', storeId, rentalType,
        rentalStart: start || undefined,
        rentalEnd: isRental ? (end || undefined) : undefined,
      });
      const st = stores?.find((s) => s.id === storeId);
      onDone(`${deviceRef(dev)} affecté à ${st?.name ?? 'ce magasin'} — ${REGIME[rentalType].label.toLowerCase()}.`);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={onClose}>
      <form className="modal" ref={formPanelRef(panelRef)} role="dialog" aria-modal="true" aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3 id={titleId}>Affecter {deviceRef(dev)}</h3>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginTop: -4, marginBottom: 4 }}>
          {dev.family}{dev.model ? ` · ${dev.model}` : ''} · {dev.serial ? <span className="mono">{dev.serial}</span> : <span className="mono">{dev.id.slice(0, 8)}</span>}
        </div>
        <label htmlFor="st">Magasin</label>
        {stores === null
          ? <div className="err">Magasins indisponibles — la liste n'a pas pu être chargée. Rechargez la page avant d'affecter.</div>
          : stores.length === 0
          ? <EmptyState compact icon="store" title="Aucun magasin" note="Créez d’abord un magasin (écran Clients) pour pouvoir affecter du matériel." />
          : <select id="st" value={storeId} onChange={(e) => setStoreId(e.target.value)} required>
              <option value="">— choisir un magasin —</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}{s.tenantName ? ` — ${s.tenantName}` : ''}{s.status !== 'active' ? ' (non provisionné)' : ''}</option>)}
            </select>}
        <span id={regimeId} className="field-title">Régime</span>
        <div role="group" aria-labelledby={regimeId} style={{ display: 'flex', gap: 6 }}>
          {(['courte', 'longue', 'achat'] as const).map((r) => {
            const on = rentalType === r;
            return (
              <button aria-pressed={on} key={r} type="button" onClick={() => setRentalType(r)}
                style={{ flex: 1, padding: '9px 6px', borderRadius: 'var(--r-sm)', cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: 600,
                  border: `1px solid ${on ? 'var(--teal)' : 'var(--ink200)'}`, background: on ? 'var(--ok-bg)' : 'transparent', color: 'var(--ink900)' }}>
                {REGIME[r].label}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="ds">{isRental ? 'Début de location' : "Date d'achat"}</label>
            <input id="ds" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          {isRental && (
            <div style={{ flex: 1 }}>
              <label htmlFor="de">Retour prévu</label>
              <input id="de" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          )}
        </div>
        {err && <div className="err">{err}</div>}
        <div className="modalfoot">
          <button type="button" className="ghost btn-ghost" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-cta" disabled={busy || !stores?.length}>{busy ? 'Affectation…' : 'Affecter'}</button>
        </div>
      </form>
    </div>
  );
}

// ── Modale : QR de MISE EN SERVICE (jeton usage unique 24 h) ──────────────────────────────────────
// Émet/affiche un QR réel : l'appareil le scanne pour tirer sa config du magasin (endpoint public /pair/redeem).
// ⚠️ §23.0 : on n'affiche « mis en service » qu'après une redemption RÉELLE (redeemedAt) ; jamais anticipé.
/** Cible minimale de la modale QR — structurelle : DeviceRow (Flotte) ou TenantDevice (fiche magasin). */
export interface PairingTarget { id: string; name: string | null; family: string; serial?: string | null; storeName?: string | null }

/**
 * Panneau « Application installée » (maquette : colonne de droite de la mise en service).
 *
 * ⚠️ CES INFORMATIONS NE VIENNENT PAS DU STORE MAIS DE L'APPAREIL — sauf la version publiée.
 * L'installation et les mises à jour appartiennent au store privé ; ce que la machine fait tourner,
 * elle seule le sait, et elle le déclare à chaque battement de synchronisation.
 *
 * ⚠️ AUCUN VERDICT SANS SES DEUX TERMES. Tant que la version publiée ou la version installée manque,
 * l'état reste factuel (« jamais synchronisée », « référence inconnue ») au lieu d'annoncer « à jour »
 * — ce qui serait le mensonge le plus coûteux ici : personne n'ira vérifier une caisse déclarée saine.
 */
export function AppInstallee({ state }: { state: DevicePairingState }) {
  const { installedVersion, declaredVersion, publishedVersion, lastBeatAt, redeemedSerial, redeemedAt, parcSerial } = state;
  // La version qui fait foi est celle du dernier battement ; celle du scan ne sert qu'à dire quelque
  // chose de vrai avant la première synchronisation.
  const installee = installedVersion ?? declaredVersion;
  const jamaisVue = !redeemedAt;
  const etat: { tone: string; label: string } =
    jamaisVue ? { tone: 'ne', label: 'en attente du premier scan' }
      : !installee ? { tone: 'ne', label: 'version non déclarée' }
        : !publishedVersion ? { tone: 'ne', label: 'référence inconnue' }
          : installee === publishedVersion ? { tone: 'ok', label: 'à jour' }
            : { tone: 'wa', label: 'une version de retard' };

  const lines: [string, string, boolean][] = [
    ['Version installée',
      jamaisVue ? 'aucune — jamais mise en service'
        : installedVersion ? installedVersion
          : declaredVersion ? `${declaredVersion} · relevée à l’appairage, pas encore confirmée`
            : 'non déclarée', true],
    ['Version au store', publishedVersion ?? 'store privé injoignable ou non configuré', true],
    // ⚠️ Le verdict se rend contre la série du PARC (saisie à l'entrée en stock), jamais contre une
    // saisie faite ici : c'est tout l'objet du contrôle anti-substitution — « est-ce bien la machine
    // que nous avons envoyée ? ». Sans série au parc, aucun verdict n'est rendu plutôt qu'un « ✓ » creux.
    ['Série déclarée',
      redeemedSerial
        ? parcSerial
          ? `${redeemedSerial} · ${redeemedSerial === parcSerial ? '✓ conforme au parc' : `⚠ le parc attend ${parcSerial}`}`
          : `${redeemedSerial} · aucune série au parc pour comparer`
        : (jamaisVue ? 'après le scan de mise en service' : 'non déclarée'), true],
    ['Dernier battement', lastBeatAt ? dateTimeShort(lastBeatAt) : (jamaisVue ? 'aucun contact — la machine parlera après le scan' : 'aucune synchronisation depuis la mise en service'), false],
  ];

  return (
    <div style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', overflow: 'hidden', alignSelf: 'start' }}>
      <div style={{ padding: '11px 14px', borderBottom: '1px solid var(--ink200)', background: 'var(--head, var(--ink-50))', display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>Application installée</span>
        <div style={{ flex: 1 }} />
        <UiPill tone={etat.tone}>{etat.label}</UiPill>
      </div>
      {lines.map(([k, v, mono]) => (
        <div key={k} style={{ display: 'flex', gap: 12, padding: '9px 14px', borderBottom: '1px solid var(--ink100)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', width: 132, flexShrink: 0 }}>{k}</span>
          <span className={mono ? 'mono' : undefined} style={{ fontSize: 'var(--t-dense)', fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{v}</span>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 9, padding: '11px 14px' }}>
        <span style={{ width: 6, height: 6, borderRadius: 'var(--r-pill)', background: 'var(--in-fg)', marginTop: 5, flexShrink: 0 }} />
        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.55 }}>
          L’installation et les mises à jour appartiennent au store privé. Ces informations ne viennent pas du store
          mais de l’appareil : une fois en service, il déclare sa version à chaque battement.
        </div>
      </div>
    </div>
  );
}

/** Modale de MISE EN SERVICE (QR à usage unique) — EXPORTÉE : la fiche magasin l'ouvre en direct
 *  (décision utilisateur 2026-08-05 : zéro détour par le registre de la flotte). */
export function PairingModal({ dev, onClose }: { dev: PairingTarget; onClose: () => void }) {
  const [state, setState] = useState<DevicePairingState | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const titleId = useId();

  useEffect(() => {
    let alive = true;
    api.devicePairing(dev.id).then((s) => { if (alive) setState(s); }).catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [dev.id]);

  const generate = async () => {
    setErr(null); setBusy(true);
    try {
      const active = await api.createPairing(dev.id);
      // Le reste de l etat (versions, battement) ne change pas : regenerer un QR ne remet rien a zero.
      setState((prev) => (prev ? { ...prev, active } : null));
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };
  const copy = async (url: string) => { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* clipboard indispo (contexte non sécurisé) — le QR reste la voie normale */ } };

  const active = state?.active ?? null;
  const hoursLeft = active ? Math.max(0, Math.round((new Date(active.expiresAt).getTime() - Date.now()) / 3600000)) : 0;

  return (
    <div className="modalwrap" onClick={onClose}>
      <div className="modal" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <h3 id={titleId}>Mise en service</h3>
        <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: -4 }}>
          {dev.name ? `${dev.name} · ` : ''}{dev.family}{dev.serial ? <> · <span className="mono">{dev.serial}</span></> : ''}{dev.storeName ? ` → ${dev.storeName}` : ''}
        </div>

        {err && <div className="err" style={{ marginTop: 12 }}>{err}</div>}
        {!state && !err && <div className="muted" style={{ marginTop: 16 }}>Chargement…</div>}

        {state?.redeemedAt && !active && (
          <div className="note" style={{ marginTop: 14 }}>
            ✅ Appareil déjà mis en service le {fmtDateTime(state.redeemedAt)}{state.redeemedSerial ? <> · série <span className="mono">{state.redeemedSerial}</span></> : ''}.
            Régénérez un QR uniquement pour re-provisionner l'appareil.
          </div>
        )}

        {/* Deux colonnes (maquette) : le JETON à gauche, ce que la machine DIT d'elle-même à droite.
            Les deux se lisent ensemble — on régénère un QR justement parce que le panneau de droite
            montre une caisse muette ou une version qui n'a pas bougé. */}
        {state && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,260px) minmax(0,1fr)', gap: 14, marginTop: 16, alignItems: 'start' }}>
            <div>
              {active ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9, padding: 16, borderRadius: 'var(--r-sm)', border: '1px solid var(--wa-fg)', background: 'var(--wa-bg)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                    <span style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>Mise en service</span>
                    <div style={{ flex: 1 }} />
                    <UiPill tone="wa">usage unique · {hoursLeft} h</UiPill>
                  </div>
                  {/* ⚠️ Blanc EN DUR, jamais `--paper` : un QR se décode par contraste, sa marge doit rester
                      blanche même en thème sombre — sinon l'appareil ne lit plus le code d'appairage. */}
                  <div style={{ background: '#fff', padding: 12, borderRadius: 'var(--r-xs)', width: 190, lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: qrSvg(active.url) }} />
                  <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink600)', textAlign: 'center', lineHeight: 1.5 }}>
                    Consommé au premier scan, expire le {fmtDateTime(active.expiresAt)}. Un QR permanent équivaudrait à une clé sans serrure.
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-ghost sm" onClick={() => void copy(active.url)}>{copied ? 'Lien copié ✓' : 'Copier le lien'}</button>
                    <button className="btn-ghost sm" disabled={busy} onClick={() => void generate()}>{busy ? '…' : 'Régénérer'}</button>
                  </div>
                </div>
              ) : (
                <button className="btn btn-cta" disabled={busy} onClick={() => void generate()} style={{ width: '100%' }}>{busy ? 'Génération…' : 'Générer le QR de mise en service'}</button>
              )}
            </div>

            <AppInstallee state={state} />
          </div>
        )}

        <p className="note" style={{ marginTop: 14 }}>
          ⚠️ Un seul QR à la fois (régénérer invalide le précédent). L'appareil le scanne pour tirer sa config du
          magasin, en déclarant son n° de série (contrôle anti-substitution). Le QR de <b>téléchargement</b> de l'app
          vit dans le store privé — celui-ci ne sert qu'à la mise en service.
        </p>
        <div className="modalfoot">
          <button type="button" className="ghost btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

type Filter = 'all' | 'location' | 'client' | 'dispo';

export function Fleet() {
  // Appareil désigné par la recherche transverse (`?device=…`) : la flotte n'a pas de route par
  // appareil, donc on surligne la ligne et on l'amène à l'écran plutôt que de rendre une liste entière
  // dans laquelle il faudrait re-chercher à l'œil ce qu'on venait de trouver.
  const [params] = useSearchParams();
  const target = params.get('device');
  const ligneCible = useRef<HTMLTableRowElement | null>(null);
  const [rows, setRows] = useState<DeviceRow[] | null>(null);
  // ⚠️ `null` = magasins NON CHARGÉS (source en échec) — distinct de « aucun magasin » : sans cette
  // distinction, une requête HS envoyait l'utilisateur créer un magasin qui existe déjà (§23.0).
  const [stores, setStores] = useState<StoreRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<DeviceRow | null>(null);
  const [assigning, setAssigning] = useState<DeviceRow | null>(null);
  const [pairing, setPairing] = useState<DeviceRow | null>(null);

  const load = async () => { try { const { devices } = await api.devices(); setRows(devices); } catch (e) { setErr((e as Error).message); } };
  useEffect(() => { void load(); void api.stores().then((r) => setStores(r.stores)).catch(() => setStores(null)); }, []);

  // Amène la ligne désignée à l'écran une fois la liste rendue : surligner une ligne restée hors du
  // champ de vision ne sert à rien — on aurait l'impression que la recherche n'a mené nulle part.
  useEffect(() => {
    if (target && rows) ligneCible.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [target, rows]);

  const act = async (id: string, fn: () => Promise<string>) => {
    setBusyId(id);
    try { showToast(await fn()); await load(); }
    catch (e) { void alert({ title: 'Action impossible', message: (e as Error).message }); }
    finally { setBusyId(null); }
  };
  // Retour au parc : passe par sa MODALE (confirmation par saisie de l'identifiant, maquette « wipe »).
  // Un appareil qui rentre a encore les données d'un client dessus — le geste ne doit pas être un
  // simple « OK » à côté d'un autre bouton rouge.
  const [returning, setReturning] = useState<DeviceRow | null>(null);
  const [renting, setRenting] = useState<DeviceRow | null>(null);
  const doRevoke = async (d: DeviceRow) => {
    const ok = await confirm({
      title: `Révoquer ${deviceRef(d)} ?`,
      message: `${deviceRef(d)} (${d.family}, ${d.serial ?? 'sans n° de série'}) sort définitivement du parc — hors service, perdu ou cassé. Il ne pourra plus être affecté. Action irréversible. Une fois révoqué, il ne reste qu’à le supprimer du registre.`,
      confirmLabel: 'Révoquer',
    });
    if (!ok) return;
    await act(d.id, async () => { await api.deviceAction(d.id, { action: 'revoke' }); return `${deviceRef(d)} révoqué (hors parc).`; });
  };
  // Supprimer : seulement un appareil RÉVOQUÉ (le serveur refuse tout autre état). Il ne porte plus aucun accès,
  // donc une confirmation simple suffit ; la trace reste au journal de la Console.
  const doDelete = async (d: DeviceRow) => {
    const name = deviceRef(d);
    const ok = await confirm({
      title: `Supprimer ${name} du parc ?`,
      message: 'Seul un appareil révoqué peut être supprimé : il ne porte plus aucun accès. Sa fiche quitte le registre, avec ses jetons d’appairage et sa configuration ; le journal de la Console garde la trace de ses opérations. Action irréversible.',
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    await act(d.id, async () => { await api.deleteDevice(d.id); return `${name} supprimé du parc.`; });
  };

  if (err) return <div className="page"><div className="err">Erreur : {err}</div></div>;
  if (!rows) return <div className="page"><div className="muted">Chargement du parc…</div></div>;

  // Sous-ensembles pour KPI/filtres. Un appareil disponible appartient au parc de LOCATION (stock affectable).
  const live = rows.filter((d) => d.status !== 'revoked');
  const inRental = (d: DeviceRow) => d.rentalType === 'courte' || d.rentalType === 'longue';
  const rentals = live.filter(inRental); // en location active (échéance suivie)
  const dueSoon = rentals.map((d) => daysLeft(d.rentalEnd)).filter((n): n is number => n !== null && n < 30);
  const overdue = dueSoon.filter((n) => n < 0).length;
  const available = live.filter((d) => d.status === 'available');

  const kpis = [
    { label: 'Parc total', n: live.length, unit: 'appareils', color: undefined as string | undefined },
    { label: 'En location', n: rentals.length, unit: 'matériel Orgazone', color: undefined },
    { label: 'Échéances < 30 j', n: dueSoon.length, unit: overdue ? `dont ${overdue} dépassée${overdue > 1 ? 's' : ''}` : 'à surveiller', color: dueSoon.length ? 'var(--wa-fg)' : undefined },
    { label: 'Disponibles', n: available.length, unit: 'affectables', color: undefined },
  ];

  const FILTERS: { k: Filter; l: string }[] = [
    { k: 'all', l: 'Tous' }, { k: 'location', l: 'Parc de location' }, { k: 'client', l: 'Matériel client' }, { k: 'dispo', l: 'Disponibles' },
  ];
  const matchFilter = (d: DeviceRow) =>
    filter === 'all' ? true
      : filter === 'dispo' ? d.status === 'available'
      : filter === 'client' ? d.rentalType === 'achat'
      : /* location */ d.status === 'available' || inRental(d);
  const list = rows.filter(matchFilter);

  // Les boutons d'une ligne selon l'état de l'appareil — [action d'état] [sortie] [modifier], alignés à droite.
  // « Modifier » est toujours l'icône la plus à droite ; un appareil révoqué garde sa place vide, pour que sa
  // corbeille tombe dans la colonne des sorties. Affecter depuis le parc, Retour au parc depuis un magasin ;
  // Révoquer depuis le parc, Supprimer une fois révoqué.
  // ⚠️ « Mise en service » n'apparaît que sur un appareil AFFECTÉ JAMAIS MIS EN SERVICE (décision utilisateur
  //    2026-10-14) : sur une caisse qui encaisse déjà, il laissait croire qu'il restait quelque chose à faire. Le
  //    marqueur est la date du scan du QR (`lastSeenAt`, posée par `/pair/redeem`, remise à zéro par une nouvelle
  //    affectation) — la règle même de la fiche magasin, qui dit « Réappairer » au lieu de « Générer le QR ».
  const rowActions = (d: DeviceRow, busy: boolean): DtAction[] => {
    const ident = d.name ?? (d.serial || d.id.slice(0, 8));
    const edit: DtAction = d.status === 'revoked'
      ? { spacer: true }
      : { label: 'Modifier', icon: 'edit', name: `Modifier ${ident}`, disabled: busy, onClick: () => setEditing(d) };
    if (d.status === 'available') return [
      { label: 'Affecter', name: `Affecter ${ident}`, disabled: busy, onClick: () => setAssigning(d) },
      { label: 'Révoquer — sortie définitive du parc', icon: 'ban', danger: true, name: `Révoquer ${ident}`, disabled: busy, onClick: () => void doRevoke(d) },
      edit,
    ];
    if (d.status === 'assigned') return [
      ...(!d.lastSeenAt
        ? [{ label: 'Mise en service', name: `Mise en service de ${ident}`, disabled: busy, onClick: () => setPairing(d) }]
        : []),
      { label: 'Retour au parc', name: `Retour au parc de ${ident}`, disabled: busy, onClick: () => setReturning(d) },
      // Un appareil dans un magasin a un contrat : son régime et ses dates se corrigent ici, sans retour au parc.
      { label: 'Régime et dates', icon: 'calendar', name: `Régime et dates de ${ident}`, disabled: busy, onClick: () => setRenting(d) },
      edit,
    ];
    if (d.status === 'revoked') return [
      { label: 'Supprimer du registre', icon: 'trash', danger: true, name: `Supprimer ${ident} du registre`, disabled: busy, onClick: () => void doDelete(d) },
      edit,
    ];
    return [edit];
  };

  return (
    <div className="page">
      {adding && <DeviceForm rows={rows} onClose={() => setAdding(false)} onDone={(n) => { setAdding(false); showToast(n); void load(); }} />}
      {editing && <DeviceForm rows={rows} device={editing} onClose={() => setEditing(null)} onDone={(n) => { setEditing(null); showToast(n); void load(); }} />}
      {assigning && <AssignDevice dev={assigning} stores={stores} onClose={() => setAssigning(null)} onDone={(n) => { setAssigning(null); showToast(n); void load(); }} />}
      {pairing && <PairingModal dev={pairing} onClose={() => setPairing(null)} />}
      {returning && <ReturnDevice dev={returning} onClose={() => setReturning(null)} onDone={(n) => { setReturning(null); showToast(n); void load(); }} />}
      {renting && <RentalModal dev={renting} onClose={() => setRenting(null)} onDone={() => void load()} />}

      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px 16px', marginBottom: 16 }}>
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          <h1 className="page-title">Appareils</h1>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3 }}>Registre du parc Orgazone : un appareil y entre « disponible », puis il est affecté à un magasin. Le suivi des locations est ici ; la facturation reste dans Sliderz Business.</div>
        </div>
        <button className="btn-cta" onClick={() => { setAdding(true); }} style={{ gap: 7, flexShrink: 0 }}>
          <Icon name="plus" size={14} />Ajouter un appareil
        </button>
      </div>


      {/* Bandeau KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(196px,1fr))', gap: 14, marginBottom: 16 }}>
        {kpis.map((k) => (
          <div key={k.label} className="dcard" style={{ padding: '14px 16px' }}>
            <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>{k.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 8 }}>
              <span style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1, color: k.color }}>{k.n}</span>
              <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>{k.unit}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filtres + recherche */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        {/* ⚠️ Puces INDÉPENDANTES, pas un segmented dans un cadre : la maquette n'enferme pas ces
            filtres. Le cadre les faisait lire comme un interrupteur à 4 positions exclusives, alors
            que ce sont quatre vues d'une même liste. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <Chip key={f.k} on={filter === f.k} onClick={() => setFilter(f.k)}>{f.l}</Chip>
          ))}
        </div>
        {/* La recherche a rejoint la barre du DataTable (maquette) : deux champs pour une même liste
            se contredisaient dès qu'on tapait dans l'un en oubliant l'autre. */}
      </div>

      {/* TABLE — DataTable partagé (maquette `fleetCols`) : recherche, tri, pagination et pied.
          Le tableau maison n'avait ni pied ni pagination : passé 40 appareils, la liste devenait un mur
          qu'on parcourait à la molette, sans jamais savoir combien il en restait. */}
      <DataTable
        // ⚠️ TROIS COLONNES ET LES ACTIONS, comme la maquette (`fleetCols`) : l'état vit SOUS le magasin — où est
        //    l'appareil, puis comment il va —, une colonne à part l'éloignait de la ligne qu'il qualifie. Le nom est borné
        //    (140 à 170 px, coupé en « … » avec l'infobulle) : un nom long ne déforme plus le tableau.
        cols={[
          { key: 'id', label: 'Appareil', minWidth: 140, maxWidth: 170 },
          { key: 'store', label: 'Magasin · état' },
          { key: 'regime', label: 'Régime · échéance' },
        ]}
        rows={list.map((d) => {
          const state = stateOf(d);
          const dl = inRental(d) ? daysLeft(d.rentalEnd) : null;
          const busy = busyId === d.id;
          return {
            id: d.id,
            cells: {
              // Trois lignes : le nom qu'on lui a donné ; la série (ce qu'on vérifie sur l'étiquette) et l'identifiant,
              // qui ne se tronque jamais ; puis le type et le modèle. Tri sur le nom — les appareils sans nom en tête,
              // ce sont eux qu'il reste à nommer.
              id: {
                v: <span style={{ fontWeight: 700, color: 'var(--ink900)' }}><DeviceName d={d} /></span>,
                sub: <>{d.serial || 'sans n° de série'} · {d.id.slice(0, 8)}</>,
                // ⚠️ SANS TYPE, l'appareil ne déclenchera rien à l'affectation (ni numéro de caisse, ni code magasin,
                //    ni licence) : c'est un fait à voir avant d'affecter, pas un détail de fiche.
                sub2: `${d.family}${d.model ? ` · ${d.model}` : ''}${d.equipmentType ? '' : ' · sans type d’équipement'}`,
                sortValue: (d.name ?? '').toLowerCase(),
                title: deviceTitle(d),
                text: [deviceTitle(d), d.serial, d.id.slice(0, 8), d.family, d.model].filter(Boolean).join(' '),
              },
              store: {
                v: d.storeName
                  ? <span style={{ fontWeight: 600 }}>
                      {d.storeName}
                      {/* N° de CAISSE attribué à l'affectation — identité fiscale (chaîne NF525 par registre). */}
                      {d.registerNumber != null && <span className="mono" style={{ marginLeft: 7, fontSize: 'var(--t-sub)', fontWeight: 700, padding: '1px 7px', borderRadius: 'var(--r-xs)', background: 'var(--ink100)', color: 'var(--ink600)' }}>R{String(d.registerNumber).padStart(2, '0')}</span>}
                      {/* IDENTITÉ MAGASIN d'un équipement hors caisse — « MP01 ». C'est ce nom que portent
                          les documents remontés par la machine, donc celui qu'on cherche ici après l'avoir
                          lu sur une zone d'inventaire. Même place et même forme que le n° de caisse : les
                          deux répondent à « comment ce magasin appelle cet appareil ». */}
                      {d.storeCode && <span className="mono" style={{ marginLeft: 7, fontSize: 'var(--t-sub)', fontWeight: 700, padding: '1px 7px', borderRadius: 'var(--r-xs)', background: 'var(--ink100)', color: 'var(--ink600)' }}>{d.storeCode}</span>}
                    </span>
                  : <span style={{ color: 'var(--ink400)' }}>non affecté</span>,
                sub: d.tenantName ?? undefined,
                // ⚠️ LE CODE ENTRE DANS LE TEXTE CHERCHÉ : c'est souvent la seule chose qu'on ait
                //    sous les yeux quand on vient d'une zone d'inventaire.
                tag: <Pill bg={state.bg} fg={state.fg}>{state.label}</Pill>,
                text: [d.storeName, d.tenantName, d.storeCode, state.label].filter(Boolean).join(' '),
              },
              regime: {
                v: d.rentalType
                  ? <Pill bg={REGIME[d.rentalType].bg} fg={REGIME[d.rentalType].fg}>{REGIME[d.rentalType].label}</Pill>
                  : <span style={{ color: 'var(--ink400)' }}>—</span>,
                // L'échéance d'une location sous son régime : « J-12 » à venir, « J+3 » dépassée (rouge), orange sous 30 jours.
                sub: dl === null ? undefined : `${dl < 0 ? `J+${Math.abs(dl)}` : `J-${dl}`} · ${fmtDay(d.rentalEnd)}`,
                subTone: dl === null ? undefined : dl < 0 ? 'da' : dl < 30 ? 'wa' : undefined,
                // ⚠️ Tri sur les JOURS RESTANTS, pas sur le libellé : « J-3 » et « J+12 » se classeraient
                // alphabétiquement, donc n'importe comment. Sans échéance = +Infinity, pour que les non-concernés
                // ne s'intercalent pas parmi les urgences.
                sortValue: dl === null ? Number.POSITIVE_INFINITY : dl,
                text: d.rentalType ? REGIME[d.rentalType].label : '',
              },
            },
            actions: rowActions(d, busy),
          };
        })}
        unit="appareil"
        searchPlaceholder="Nom, série, magasin, client…"
        pageSize={10}
        emptyIcon="tablet"
        emptyTitle={rows.length === 0 ? 'Aucun appareil au parc' : 'Aucun appareil dans ce périmètre'}
        emptyNote={rows.length === 0
          ? 'Ajoutez du matériel (« + Ajouter un appareil ») pour commencer à équiper les magasins.'
          : 'Filtrez sur « Tous » pour voir le parc entier, ou affectez un appareil depuis la fiche d’un magasin.'}
        focusId={target ?? undefined}
      />
    </div>
  );
}
